import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  approved: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300',
  rejected: 'bg-rose-100 text-rose-800 ring-1 ring-rose-300',
  pending: 'bg-slate-100 text-slate-700 ring-1 ring-slate-300',
};

/**
 * Shown on a provider/doctor profile. Pulls /api/verification/my-status
 * and surfaces:
 *   - verified state (green, celebratory)
 *   - blocked state (red)
 *   - otherwise a progress bar + list of rejected / pending required fields
 *     with admin notes so the user knows what to fix.
 */
const VerificationStatusBanner = ({ sessionToken }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    if (!sessionToken) return;
    try {
      const res = await fetch(`${API}/api/verification/my-status`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      if (res.ok) {
        setData(await res.json());
      }
    } catch (e) {
      console.error('verification status error', e);
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  if (loading || !data || !data.applicable) return null;

  if (data.is_blocked) {
    return (
      <div
        className="bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-start gap-3 mb-4"
        data-testid="verification-blocked-banner"
      >
        <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0">
          <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <div>
          <p className="font-semibold text-rose-800">{t('verificationStatus.blocked')}</p>
          <p className="text-sm text-rose-600">
            {t('verificationStatus.blockedSub')}
          </p>
        </div>
      </div>
    );
  }

  if (data.is_verified) {
    return (
      <div
        className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3 mb-4"
        data-testid="verification-verified-banner-status"
      >
        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
          <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <p className="font-semibold text-emerald-800">{t('verificationStatus.verified')}</p>
          <p className="text-sm text-emerald-600">{t('verificationStatus.verifiedSub')}</p>
        </div>
      </div>
    );
  }

  const progress = data.verification_progress || { approved_count: 0, required_count: 0 };
  const fieldLabel = (f) => t(`verificationStatus.fields.${f.field}`, f.label || f.field);
  const rejected = data.rejected_fields || [];
  const pendingReq = (data.pending_required_fields || []).filter((f) => f.status === 'pending');
  const needsAttention = rejected.length > 0;

  return (
    <div
      className={`rounded-lg border p-4 mb-4 ${
        needsAttention ? 'bg-amber-50 border-amber-300' : 'bg-blue-50 border-blue-200'
      }`}
      data-testid="verification-status-banner"
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            needsAttention ? 'bg-amber-100' : 'bg-blue-100'
          }`}
        >
          <svg
            className={`w-6 h-6 ${needsAttention ? 'text-amber-600' : 'text-blue-600'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={
                needsAttention
                  ? 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                  : 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
              }
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <p className={`font-semibold ${needsAttention ? 'text-amber-900' : 'text-blue-900'}`}>
              {needsAttention
                ? t('verificationStatus.needAttention', { count: rejected.length })
                : t('verificationStatus.inProgress')}
            </p>
            <span
              className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                needsAttention ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
              }`}
            >
              {t('verificationStatus.approvedCount', { approved: progress.approved_count, required: progress.required_count })}
            </span>
          </div>
          <div className="w-full h-2 rounded-full bg-white/70 overflow-hidden mb-3">
            <div
              className={`h-full transition-all ${needsAttention ? 'bg-amber-500' : 'bg-blue-500'}`}
              style={{
                width: `${
                  progress.required_count ? (progress.approved_count / progress.required_count) * 100 : 0
                }%`,
              }}
            />
          </div>

          {rejected.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide mb-2">
                {t('verificationStatus.rejectedTitle')}
              </p>
              <ul className="space-y-2">
                {rejected.map((r) => (
                  <li
                    key={r.field}
                    className="bg-white rounded-md border border-amber-200 px-3 py-2 text-sm"
                    data-testid={`rejected-field-${r.field}`}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE.rejected}`}>
                        {t('verificationStatus.rejectedChip')}
                      </span>
                      <span className="font-medium text-gray-900">{fieldLabel(r)}</span>
                    </div>
                    {r.note ? (
                      <p className="text-xs text-gray-600 mt-1">
                        <span className="font-medium">{t('verificationStatus.adminNote')}</span> {r.note}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500 italic mt-1">
                        {t('verificationStatus.noNote')}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {pendingReq.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                {t('verificationStatus.stillWaiting')}
              </p>
              <div className="flex flex-wrap gap-2">
                {pendingReq.map((p) => (
                  <span
                    key={p.field}
                    className="text-xs px-2 py-1 rounded-md bg-white border border-gray-200 text-gray-700"
                    data-testid={`pending-field-${p.field}`}
                  >
                    {fieldLabel(p)}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-3">
            <button
              type="button"
              onClick={fetchStatus}
              className="text-xs font-medium text-blue-600 hover:text-blue-800 underline"
              data-testid="verification-refresh-btn"
            >
              {t('verificationStatus.refresh')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationStatusBanner;
